import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";

const FilterPanel = ({
  filters,
  onFilterChange,
  onClearFilters,
  isOpen,
  onToggle,
  projectCount,
  maxLimit = 2000,
}) => {
  const propertyTypeOptions = [
    { value: "", label: "Todos los tipos" },
    { value: "Residencial", label: "Residencial" },
    { value: "Comercial", label: "Comercial" },
    { value: "Agrícola", label: "Agrícola" },
  ];

  const locationOptions = [
    { value: "", label: "Todas las ubicaciones" },
    { value: "Guatemala City", label: "Ciudad de Guatemala" },
    { value: "Antigua", label: "Antigua Guatemala" },
    { value: "Quetzaltenango", label: "Quetzaltenango" },
    { value: "Escuintla", label: "Escuintla" },
    { value: "Chimaltenango", label: "Chimaltenango" },
    { value: "Sacatepéquez", label: "Sacatepéquez" },
  ];

  const systemSizeOptions = [
    { value: "", label: "Todos los tamaños" },
    { value: "1-10", label: "1-10 kW (Residencial pequeño)" },
    { value: "10-20", label: "10-20 kW (Residencial mediano)" },
    { value: "20-30", label: "20-30 kW (Residencial grande)" },
    { value: "30-40", label: "30-40 kW (Comercial pequeño)" },
    { value: "40-50", label: "40-50 kW (Comercial mediano)" },
    { value: "50+", label: "50+ kW (Comercial grande)" },
  ];


  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Object.values(filters)?.some(
    (value) => value !== ""
  );

  return (
    <>
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-6">
        <Button
          variant="outline"
          onClick={onToggle}
          iconName="Filter"
          iconPosition="left"
          className="w-full"
        >
          Filtros{" "}
          {hasActiveFilters &&
            `(${Object.values(filters)?.filter((v) => v)?.length})`}
        </Button>
      </div>
      {/* Filter Panel */}
      <div
        className={`bg-white rounded-xl solar-shadow-card p-6 ${isOpen ? "block" : "hidden lg:block"
          }`}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground flex items-center">
            <Icon name="Filter" size={20} className="mr-2" />
            Filtrar Proyectos
          </h3>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Limpiar
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {/* Property Type Filter */}
          <Select
            label="Tipo de Propiedad"
            options={propertyTypeOptions}
            value={filters?.type}
            onChange={(value) => handleFilterChange("type", value)}
          />

          {/* Location Filter */}
          <Select
            label="Ubicación"
            options={locationOptions}
            value={filters?.location}
            onChange={(value) => handleFilterChange("location", value)}
            searchable
          />

          {/* System Size Filter */}
          <Select
            label="Tamaño del Sistema"
            options={systemSizeOptions}
            value={filters?.systemSize}
            onChange={(value) => handleFilterChange("systemSize", value)}
          />

          {/* Monthly Generation Range Filter */}
          <div className="space-y-4 pt-2">
            <label className="text-sm font-medium text-foreground">
              Generación Mensual (kWh)
            </label>

            <div className="space-y-6 px-1">
              {/* Range Slider Container */}
              <div className="relative pt-4 pb-2">
                <div className="relative h-2 w-full bg-gray-200 rounded-full">
                  {/* Track highlight */}
                  <div
                    className="absolute h-full bg-primary rounded-full transition-all duration-150 shadow-[0_0_12px_rgba(59,130,246,0.3)]"
                    style={{
                      left: `${(filters?.minGen / maxLimit) * 100}%`,
                      width: `${((filters?.maxGen - filters?.minGen) / maxLimit) * 100}%`
                    }}
                  />

                  {/* Dual Thumbs Container */}
                  <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                    <input
                      type="range"
                      min="0"
                      max={maxLimit}
                      step={Math.max(1, Math.floor(maxLimit / 100))}
                      value={filters?.minGen}
                      onChange={(e) => {
                        const gap = maxLimit * 0.05;
                        const val = Math.min(Number(e.target.value), filters?.maxGen - gap);
                        handleFilterChange("minGen", Math.max(0, val));
                      }}
                      style={{ zIndex: filters?.minGen > maxLimit / 2 ? 40 : 30 }}
                      className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none cursor-pointer hover:z-50 active:z-50 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:appearance-none [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:shadow-lg"
                    />
                    <input
                      type="range"
                      min="0"
                      max={maxLimit}
                      step={Math.max(1, Math.floor(maxLimit / 100))}
                      value={filters?.maxGen}
                      onChange={(e) => {
                        const gap = maxLimit * 0.05;
                        const val = Math.max(Number(e.target.value), filters?.minGen + gap);
                        handleFilterChange("maxGen", Math.min(maxLimit, val));
                      }}
                      style={{ zIndex: filters?.maxGen < maxLimit / 2 ? 40 : 30 }}
                      className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none cursor-pointer hover:z-50 active:z-50 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:appearance-none [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:shadow-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Number Inputs */}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <span className="block text-[10px] text-muted-foreground uppercase font-bold mb-1.5 ml-1">De</span>
                  <div className="relative group">
                    <input
                      type="number"
                      value={Math.round(filters?.minGen)}
                      onChange={(e) => handleFilterChange("minGen", Math.max(0, Number(e.target.value)))}
                      className="w-full pl-3 pr-9 py-2 text-sm font-medium border-2 rounded-xl bg-gray-50/50 group-hover:bg-white focus:bg-white focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground/60">kWh</span>
                  </div>
                </div>
                <div className="flex-1">
                  <span className="block text-[10px] text-muted-foreground uppercase font-bold mb-1.5 ml-1">Hasta</span>
                  <div className="relative group">
                    <input
                      type="number"
                      value={Math.round(filters?.maxGen)}
                      onChange={(e) => handleFilterChange("maxGen", Math.min(maxLimit, Math.max(filters?.minGen, Number(e.target.value))))}
                      className="w-full pl-3 pr-9 py-2 text-sm font-medium border-2 rounded-xl bg-gray-50/50 group-hover:bg-white focus:bg-white focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all outline-none"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-muted-foreground/60">kWh</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-6 pt-4 border-t">
          <div className="flex items-center text-sm text-muted-foreground">
            <Icon name="Search" size={16} className="mr-2" />
            {projectCount} proyecto{projectCount !== 1 ? "s" : ""} encontrado
            {projectCount !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Quick Filters */}
        <div className="mt-4">
          <div className="text-sm font-medium text-foreground mb-2">
            Filtros Rápidos
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => handleFilterChange("type", "Residencial")}
              className={
                filters?.type === "Residencial"
                  ? "bg-primary/10 text-primary"
                  : ""
              }
            >
              Residencial
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => handleFilterChange("location", "Guatemala City")}
              className={
                filters?.location === "Guatemala City"
                  ? "bg-primary/10 text-primary"
                  : ""
              }
            >
              Guatemala City
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => handleFilterChange("systemSize", "1-10")}
              className={
                filters?.systemSize === "1-10"
                  ? "bg-primary/10 text-primary"
                  : ""
              }
            >
              1-10 kW
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;
